import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EmployeeJobBreakService } from '../../../services/roster/employee-job-break.service';
import { SnackbarService } from '../../../common/services/snackbar.service';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../common/components/confirm-dialog/confirm-dialog.component';
import moment from 'moment';

@Component({
  selector: 'app-employee-job-break-dialog',
  templateUrl: './employee-job-break-dialog.component.html',
  styleUrls: ['./employee-job-break-dialog.component.scss'],
  standalone: false
})
export class EmployeeJobBreakDialogComponent implements OnInit {
  form: FormGroup;
  breaks: any[] = [];
  leaveTypes: any[] = [];
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private breakService: EmployeeJobBreakService,
    private snackBar: SnackbarService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<EmployeeJobBreakDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({
      id: [null],
      employee_id: [data.employee.id, Validators.required],
      start_date: [null, Validators.required],
      end_date: [null, Validators.required],
      leave_type_id: [null, Validators.required],
      remarks: ['']
    });
  }

  ngOnInit(): void {
    this.loadLeaveTypes();
    this.loadBreaks();
  }

  loadLeaveTypes() {
    this.breakService.getLeaveTypes().subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.leaveTypes = (res.data || []).sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );
      }
    });
  }

  loadBreaks() {
    this.breakService.getBreaks(this.data.employee.id).subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.breaks = res.data;
      }
    });
  }

  onSave() {
    if (this.form.invalid) return;

    const rawData = this.form.value;
    const data = {
      ...rawData,
      start_date: moment(rawData.start_date).format('YYYY-MM-DD'),
      end_date: moment(rawData.end_date).format('YYYY-MM-DD')
    };

    if (moment(data.start_date).isAfter(data.end_date)) {
      this.snackBar.open('Start date cannot be after end date', 'error');
      return;
    }

    this.isSaving = true;
    this.breakService.saveBreak(data).subscribe((res: any) => {
      if (res.status === 'SUCCESS') {
        this.snackBar.open(res.message, 'success');
        this.resetForm();
        this.loadBreaks();
      } else {
        this.snackBar.open(res.message, 'error');
      }
      this.isSaving = false;
    });
  }

  onEdit(item: any) {
    this.form.patchValue({
      id: item.id,
      start_date: moment.utc(item.start_date, 'YYYY-MM-DD').toDate(),
      end_date: moment.utc(item.end_date, 'YYYY-MM-DD').toDate(),
      leave_type_id: item.leave_type_id,
      remarks: item.remarks
    });
  }

  onDelete(item: any) {
    const dialogData = new ConfirmDialogModel('Confirm Delete', 'Are you sure you want to delete this job break?');
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.breakService.deleteBreak(item.id).subscribe((res: any) => {
          if (res.status === 'SUCCESS') {
            this.snackBar.open(res.message, 'success');
            this.loadBreaks();
            if (this.form.get('id')?.value === item.id) {
              this.resetForm();
            }
          } else {
            this.snackBar.open(res.message, 'error');
          }
        });
      }
    });
  }

  resetForm() {
    this.form.reset({
      id: null,
      employee_id: this.data.employee.id,
      start_date: null,
      end_date: null,
      leave_type_id: null,
      remarks: ''
    });
  }

  onClose() {
    this.dialogRef.close(this.breaks);
  }
}
